import { useDocumentTitle } from "@uidotdev/usehooks";

import React, { FC } from "react";
import { FaPlus } from "react-icons/fa";
import { config } from "../config";
import { ApiContextType, useApi, useTranslations } from "../contexts";
import { routerPaths } from "./routerPaths";

import { Link, Navigate } from "react-router-dom";
import { NonUndefined, ValuesType } from "utility-types";
import {
  Button,
  CardSkeleton,
  InlineSpinner,
  LoadingErrorState,
  PageSkeleton,
} from "../components";

const SiteListItem: React.FC<{
  site: ValuesType<NonUndefined<NonNullable<ApiContextType["sites"]>["data"]>>;
}> = (props) => {
  const api = useApi();
  const i18n = useTranslations();
  const siteQuery = api.siteById({ siteId: props.site.id });
  if (!siteQuery || siteQuery.isPending || siteQuery.isLoading) {
    return <CardSkeleton />;
  }
  if (siteQuery.isError || !siteQuery.data) {
    return (
      <LoadingErrorState
        message={i18n["Failed to load this site."]}
        className="h-full"
        retryLabel={i18n["Retry"]}
        onRetry={() => siteQuery.refetch()}
      />
    );
  }

  const site = siteQuery.data;

  return (
    <Link
      className="block max-w-sm border-2 p-5 rounded hover:bg-slate-100"
      to={routerPaths.siteManage({ siteId: site.id, tab: "general" })}
    >
      <h5 className="text-2xl font-bold first-letter:uppercase">
        {site.name || site.id}
      </h5>
      <p className="font-normal text-gray-700 dark:text-gray-400 line-clamp-2 first-letter:uppercase">
        {site.hero?.description}
      </p>
    </Link>
  );
};

export const DashboardPage: FC = () => {
  const api = useApi();
  const i18n = useTranslations();
  useDocumentTitle(`${config.app.name} - ${i18n["dashboard"]}`);
  const sitesQuery = api.sites;

  if (!sitesQuery || sitesQuery.isPending || sitesQuery.isLoading) {
    return (
      <PageSkeleton
        className="px-3"
        cards={6}
        ariaLabel={i18n["Loading page content"]}
        loadingLabel={i18n["Loading..."]}
      />
    );
  }
  if (sitesQuery.isError) {
    return (
      <LoadingErrorState
        className="mx-3 mt-3"
        message={i18n["Something went wrong"]}
        retryLabel={i18n["Retry"]}
        onRetry={() => sitesQuery.refetch()}
      />
    );
  }

  if (
    !sitesQuery.isLoading &&
    sitesQuery.isFetched &&
    sitesQuery.data?.length === 0
  ) {
    return <Navigate to={routerPaths.siteNew} />;
  }
  return (
    <div>
      <h1 className="text-2xl font-bold border-b p-3 mb-6 flex">
        <p className="grow">{i18n["sites"]}</p>
        {sitesQuery.isFetching && (
          <InlineSpinner
            className="mr-2 text-slate-500"
            size="sm"
            label={i18n["Refreshing sites"]}
          />
        )}

        <Button linkTo={routerPaths.siteNew}>
          <div className="flex items-center gap-1">
            <FaPlus className="mr-3 h-4 w-4" />
            {i18n.new}
          </div>
        </Button>
      </h1>
      <ul className="grid grid-cols-1 gap-3 md:grid-cols-3" aria-busy={sitesQuery.isFetching}>
        {sitesQuery.data?.map((site) => (
          <SiteListItem key={site.id} site={site} />
        ))}
      </ul>
    </div>
  );
};
